import { useEffect, useRef, useState } from 'react';
import { Check, Loader2, Plus, Search } from 'lucide-react';
import { createAdminSymbol, searchAvailableSymbols, type AvailableSymbol } from '../../lib/symbols';

interface MassiveTickerSearchProps {
  onCatalogChanged: () => Promise<void>;
  getNextSortOrder: () => number;
}

interface AddProgress {
  done: number;
  total: number;
  current: string;
}

const SEARCH_DEBOUNCE_MS = 300;

export function MassiveTickerSearch({
  onCatalogChanged,
  getNextSortOrder,
}: MassiveTickerSearchProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<AvailableSymbol[] | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [addProgress, setAddProgress] = useState<AddProgress | null>(null);
  const [addSummary, setAddSummary] = useState<string | null>(null);
  const [addFailures, setAddFailures] = useState<string[]>([]);
  const searchSequenceRef = useRef(0);

  useEffect(() => {
    const search = query.trim();
    const sequence = ++searchSequenceRef.current;
    const timeout = setTimeout(() => {
      if (search === '') {
        setResults(null);
        setIsSearching(false);
        setSearchError(null);
        return;
      }

      setIsSearching(true);
      searchAvailableSymbols(search).then(
        (found) => {
          if (searchSequenceRef.current !== sequence) {
            return;
          }
          setSearchError(null);
          setResults(found);
          setIsSearching(false);
        },
        (error: unknown) => {
          if (searchSequenceRef.current !== sequence) {
            return;
          }
          setSearchError(error instanceof Error ? error.message : 'Massive ticker search failed');
          setResults(null);
          setIsSearching(false);
        },
      );
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timeout);
  }, [query]);

  const isAdding = addProgress != null;

  const toggleSelected = (ticker: string) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(ticker)) {
        next.delete(ticker);
      } else {
        next.add(ticker);
      }
      return next;
    });
  };

  const addSymbols = async (candidates: AvailableSymbol[]) => {
    if (candidates.length === 0 || isAdding) {
      return;
    }

    setAddSummary(null);
    setAddFailures([]);
    const added = new Set<string>();
    const failures: string[] = [];
    let sortOrder = getNextSortOrder();

    for (const [index, candidate] of candidates.entries()) {
      setAddProgress({ done: index, total: candidates.length, current: candidate.ticker });
      try {
        // Label and asset type come from Massive's reference listing; nothing
        // renders them today, but sourced-from-API values beat placeholders.
        await createAdminSymbol({
          ticker: candidate.ticker,
          label: candidate.name || candidate.ticker,
          assetType: candidate.type === 'ETF' ? 'etf' : 'stock',
          enabled: true,
          sortOrder,
        });
        added.add(candidate.ticker);
        sortOrder += 10;
      } catch (error) {
        const message = error instanceof Error ? error.message : 'add failed';
        failures.push(`${candidate.ticker}: ${message}`);
      }
    }

    setAddProgress(null);
    setAddFailures(failures);
    setAddSummary(`Added ${added.size} of ${candidates.length}`);
    setSelected((current) => {
      const next = new Set(current);
      added.forEach((ticker) => next.delete(ticker));
      return next;
    });
    setResults((current) =>
      current == null
        ? current
        : current.map((result) =>
            added.has(result.ticker) ? { ...result, supported: true } : result,
          ),
    );

    if (added.size > 0) {
      await onCatalogChanged();
    }
  };

  const selectedCandidates =
    results?.filter((result) => selected.has(result.ticker) && !result.supported) ?? [];

  return (
    <div>
      <label className="flex h-8 items-center gap-2 rounded-md border border-white/[.08] bg-white/[.03] px-3 transition-colors focus-within:border-white/[.2]">
        <Search className="h-3.5 w-3.5 shrink-0 text-neutral-600" />
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search Massive for tickers or company names"
          aria-label="Search Massive tickers"
          className="min-w-0 flex-1 bg-transparent font-mono text-[11px] text-neutral-100 outline-none placeholder:text-neutral-600"
        />
        {isSearching && <Loader2 className="h-3 w-3 shrink-0 animate-spin text-neutral-500" />}
      </label>

      {searchError != null && (
        <p className="mt-2 font-mono text-[10px] text-red-400">{searchError}</p>
      )}

      {results != null && (
        <div className="mt-2 rounded-md border border-white/[.08] bg-white/[.02]">
          <div className="max-h-64 overflow-y-auto">
            {results.length === 0 ? (
              <p className="px-3 py-4 text-center font-mono text-[10px] text-neutral-500">
                No matches in Massive
              </p>
            ) : (
              results.map((result) => (
                <div
                  key={result.ticker}
                  className="grid grid-cols-[auto_auto_1fr_auto] items-center gap-2.5 border-b border-white/[.05] px-3 py-2 last:border-b-0"
                >
                  <input
                    type="checkbox"
                    checked={selected.has(result.ticker)}
                    disabled={result.supported || isAdding}
                    onChange={() => toggleSelected(result.ticker)}
                    aria-label={`Select ${result.ticker}`}
                    className="h-3 w-3 accent-green-500 disabled:opacity-30"
                  />
                  <span className="w-14 truncate font-mono text-[11px] font-semibold text-neutral-100">
                    {result.ticker}
                  </span>
                  <span className="truncate font-mono text-[10px] text-neutral-500">
                    {result.name}
                  </span>
                  {result.supported ? (
                    <span className="inline-flex items-center gap-1 font-mono text-[9px] font-semibold tracking-[0.05em] text-green-500">
                      <Check className="h-3 w-3" />
                      ADDED
                    </span>
                  ) : (
                    <button
                      type="button"
                      disabled={isAdding}
                      onClick={() => void addSymbols([result])}
                      aria-label={`Add ${result.ticker}`}
                      className="flex h-5 w-5 items-center justify-center rounded border border-white/[.08] bg-white/[.03] text-neutral-400 transition-all duration-150 hover:bg-white/[.08] hover:text-neutral-100 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      <Plus className="h-3 w-3" />
                    </button>
                  )}
                </div>
              ))
            )}
          </div>

          {selectedCandidates.length > 0 && (
            <div className="flex items-center justify-between gap-2 border-t border-white/[.08] px-3 py-2">
              <span className="font-mono text-[10px] text-neutral-500">
                {selectedCandidates.length} selected
              </span>
              <button
                type="button"
                disabled={isAdding}
                onClick={() => void addSymbols(selectedCandidates)}
                className="inline-flex h-6 items-center gap-1.5 rounded-md border border-green-500/30 bg-green-500/15 px-2 font-mono text-[10px] font-semibold text-green-500 transition-all duration-150 hover:bg-green-500/25 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Plus className="h-3 w-3" />
                Add {selectedCandidates.length} selected
              </button>
            </div>
          )}
        </div>
      )}

      {addProgress != null && (
        <p
          role="status"
          className="mt-2 inline-flex items-center gap-1.5 font-mono text-[10px] text-neutral-400"
        >
          <Loader2 className="h-3 w-3 animate-spin" />
          Validating &amp; adding {addProgress.done + 1}/{addProgress.total} · {addProgress.current}
        </p>
      )}

      {addSummary != null && addProgress == null && (
        <p role="status" className="mt-2 font-mono text-[10px] text-green-500">
          {addSummary}
        </p>
      )}

      {addFailures.length > 0 && addProgress == null && (
        <ul className="mt-1 space-y-0.5 font-mono text-[10px] text-red-400">
          {addFailures.map((failure) => (
            <li key={failure}>{failure}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
